import { FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useLogin } from '../features/auth/useAuth';
import { getApiErrorMessage } from '../lib/errors';
import { Field } from '../components/ui/Field';

export function LoginPage() {
  const isAuth = useAuthStore((s) => s.isAuthenticated());
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (isAuth) return <Navigate to="/panorama" replace />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const destination = (location.state as { from?: string } | null)?.from ?? '/panorama';
    login.mutate(
      { email, password },
      { onSuccess: () => navigate(destination, { replace: true }) },
    );
  };

  return (
    <AuthShell title="Iniciar sesión" subtitle="Entra a tu Asistente Financiero">
      <form onSubmit={onSubmit} className="space-y-4">
        {(
          location.state as {
            passwordChanged?: boolean;
            passwordReset?: boolean;
            accountDeleted?: boolean;
          } | null
        )?.passwordChanged && (
          <p className="text-sm text-income">Contraseña actualizada. Inicia sesión de nuevo.</p>
        )}
        {(
          location.state as {
            passwordChanged?: boolean;
            passwordReset?: boolean;
            accountDeleted?: boolean;
          } | null
        )?.passwordReset && (
          <p className="text-sm text-income">Contraseña restablecida. Ya puedes iniciar sesión.</p>
        )}
        {(
          location.state as {
            passwordChanged?: boolean;
            passwordReset?: boolean;
            accountDeleted?: boolean;
          } | null
        )?.accountDeleted && (
          <p className="text-sm text-income">
            Tu cuenta y datos de la aplicación fueron eliminados.
          </p>
        )}
        <Field
          id="email"
          label="Correo"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          id="password"
          label="Contraseña"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Link
          to="/restablecer"
          className="tap-target flex items-center justify-end text-sm font-semibold text-slate-700 underline"
        >
          Olvidé mi contraseña
        </Link>
        {login.isError && (
          <p role="alert" className="text-sm text-expense">
            {getApiErrorMessage(login.error)}
          </p>
        )}
        <button
          type="submit"
          disabled={login.isPending}
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {login.isPending ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
      <p className="mt-4 text-center text-sm text-slate-600">
        ¿Aún no tienes cuenta?{' '}
        <Link
          to="/registro"
          className="tap-target inline-flex items-center font-semibold text-slate-900 underline"
        >
          Créala gratis
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10 pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))] pt-safe">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[14px] bg-slate-900 text-white">
            <svg
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M4 20V11M10 20V4M16 20v-6M3 20h18" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">{children}</div>
      </div>
    </div>
  );
}
